if (!window.customElements.get('lookbook-popup')) {
  class LookbookPopup extends Drawer {
    constructor() {
      super();
    }

    connectedCallback() {
      if (this.parentElement !== document.body) {
        this._closeAndRemoveStalePortals();
        document.body.appendChild(this);
      } else {
        this._whenSectionStylesReady(() => this.classList.add('lookbook-popup--ready'));
      }
    }

    _whenSectionStylesReady(callback) {
      const pending = [...document.querySelectorAll('link[href*="section-gallery-lookbook"]')].filter(
        (link) => !link.sheet
      );

      if (!pending.length) {
        callback();
        return;
      }

      let remaining = pending.length;
      const done = () => {
        if (--remaining === 0) callback();
      };

      pending.forEach((link) => {
        link.addEventListener('load', done, { once: true });
        link.addEventListener('error', done, { once: true });
      });
    }

    _handle_ShowDrawer_Action(e) {
      super._handle_ShowDrawer_Action(e);

      if (!this.isConnected || e.detail.drawerType !== this.drawerType) return;

      this._pauseOutsideVideos();

      setTimeout(() => this._playOwnVideo(), 150);
    }

    _playOwnVideo() {
      const player = this.querySelector('reel-feed-player[media-autoplay]');
      if (!player || !this.isDrawerOpen) return;

      player._resetVideo?.();
      player._renderMedia?.();
    }

    _handle_HideDrawer_Action(e) {
      const wasOpen = this.isDrawerOpen;

      super._handle_HideDrawer_Action(e);

      if (wasOpen) {
        const videoPlayer = this.querySelector('reel-feed-player');
        if (videoPlayer?._pause) videoPlayer._pause();
      }

      this._scheduleResumeOutsideVideos();
    }

    _pauseOutsideVideos() {
      clearTimeout(this._resumeTimer);

      this._pausedOutside = [...document.querySelectorAll('reel-feed-player')].filter(
        (player) => !this.contains(player) && player.getAttribute('video-playing') === 'true'
      );

      this._pausedOutside.forEach((player) => player._pause?.());
    }

    _scheduleResumeOutsideVideos() {
      if (!this._pausedOutside?.length) return;

      clearTimeout(this._resumeTimer);
      this._resumeTimer = setTimeout(() => this._resumeOutsideVideos(), 200);
    }

    _resumeOutsideVideos() {
      const overlayOpen = [
        ...document.querySelectorAll('lookbook-popup, drawer-component, cart-drawer-component'),
      ].some((el) => el.isDrawerOpen);

      if (overlayOpen) return;

      this._pausedOutside?.forEach((player) => {
        if (player.isConnected && player.getAttribute('video-playing') !== 'true') player._play?.();
      });

      this._pausedOutside = [];
    }

    _closeAndRemoveStalePortals() {
      const drawerType = this.getAttribute('drawer-type');
      document.querySelectorAll(`lookbook-popup[drawer-type="${drawerType}"]`).forEach((el) => {
        if (el === this) return;
        el.removePortal();
      });
      HELPER_UTIL.bodyInteractions.enableScroll();
    }

    removePortal() {
      this.classList.remove('drawer-active');
      this.isDrawerOpen = false;
      this.remove();
    }
  }

  customElements.define('lookbook-popup', LookbookPopup);

  document.addEventListener('shopify:section:unload', (e) => {
    const sectionId = e.detail?.sectionId;
    if (!sectionId) return;

    const popups = document.querySelectorAll(`lookbook-popup[data-section-id="${sectionId}"]`);
    if (!popups.length) return;

    popups.forEach((popup) => popup.removePortal());

    HELPER_UTIL.bodyInteractions.enableScroll();
  });

  const initLookbookPlayers = () => {
    document.querySelectorAll('.gallery-lookbook reel-feed-player, lookbook-popup reel-feed-player').forEach((player) => {
      if (player.dataset.lookbookPlayerBound) return;
      player.dataset.lookbookPlayerBound = 'true';

      const video = player.player;
      if (!video) return;

      const markFramePainted = () => player.classList.add('video-has-frame');

      if (typeof video.requestVideoFrameCallback === 'function') {
        video.requestVideoFrameCallback(markFramePainted);
      } else {
        const onFirstTimeUpdate = () => {
          if (video.currentTime <= 0) return;
          video.removeEventListener('timeupdate', onFirstTimeUpdate);
          markFramePainted();
        };
        video.addEventListener('timeupdate', onFirstTimeUpdate);
      }

      const popup = player.closest('lookbook-popup');
      if (popup) {
        video.addEventListener('play', () => {
          if (!popup.isDrawerOpen) player._pause?.();
        });
      }

      player.addEventListener('click', (e) => {
        if (!e.target.closest('[play-media]')) return;

        video.play()?.catch(() => {
          video.muted = true;
          player.audioEl?.setAttribute('audio-state', 'mute');

          video.play()?.catch(() => {
            player._pause?.();
          });
        });
      });
    });
  };

  document.addEventListener('DOMContentLoaded', initLookbookPlayers);
  document.addEventListener('shopify:section:load', initLookbookPlayers);

  const fillLookbookPopup = (trigger, drawerType) => {
    const popup = document.querySelector(`lookbook-popup[drawer-type="${drawerType}"]`);
    const template = trigger.querySelector('template[lookbook-popup-content]');
    const target = popup?.querySelector('[lookbook-popup-content]');
    if (!popup || !template || !target) return false;

    const blockId = trigger.dataset.lookbookBlock;
    if (blockId && target.dataset.lookbookBlock === blockId) return true;

    target.querySelectorAll('video').forEach((video) => video.pause());

    target.innerHTML = '';
    target.appendChild(template.content.cloneNode(true));
    target.dataset.lookbookBlock = blockId;

    initLookbookPlayers();

    return true;
  };

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[gallery-popup-trigger]');
    if (!btn) return;
    const drawerType = btn.getAttribute('gallery-popup-trigger');
    if (!drawerType) return;
    if (!fillLookbookPopup(btn, drawerType)) return;
    window.currentActiveElement = document.activeElement;
    HELPER_UTIL.dispatchCustomEvent(_EVENT_HELPER.showDrawer, btn, {
      drawerType: drawerType,
    });
  });

  const muteLookbookPlayersExcept = (current) => {
    document
      .querySelectorAll('.gallery-lookbook reel-feed-player, lookbook-popup reel-feed-player')
      .forEach((player) => {
        if (player === current) return;

        const audioEl = player.querySelector('[audio-state]');
        if (!audioEl || audioEl.getAttribute('audio-state') !== 'unmute') return;

        audioEl.setAttribute('audio-state', 'mute');
        player.audioState = 'mute';
        player.playMuteVideo = true;
        if (player.player) player.player.muted = true;
      });
  };

  document.addEventListener('click', (e) => {
    const audioEl = e.target.closest('[audio-state]');
    if (!audioEl) return;

    const player = audioEl.closest('reel-feed-player');
    if (!player || !player.closest('.gallery-lookbook, lookbook-popup')) return;

    const state = audioEl.getAttribute('audio-state');
    player.audioState = state;
    player.playMuteVideo = state === 'mute';

    if (state !== 'unmute') return;

    muteLookbookPlayersExcept(player);
  });
}
//# sourceMappingURL=gallery-lookbook.js.map
